/**
 * The home, list and hub route modules' document answer (web plan W4.5):
 * src/routes/{home,promotions,brands,shops,category,city,brandCity,sale,sales,nearby}.tsx.
 *
 * Like the entity routes' (@/seo/entityDocument), the loader reads the SEO
 * server's decision for the URL through the load context (`seo.page()`,
 * server/framework.mjs): the answer the root loader reads (one `decide` per
 * request), plus the data the page's own queries would load (`preload`,
 * @/seo/routePreload), so the server-rendered body lists what the head and
 * its JSON-LD describe.
 *
 * - 301 / 302 → a thrown `redirect` with the decision's status, never
 *   `redirect()`'s default (lists and hubs only ever answer 301: `?page=1`,
 *   `?page=03`, another spelling, the URL move; the home page's moves are
 *   W1's 302s), Location as the server built it (query kept);
 * - 404 / 410 (a page past the end, a malformed `?page=`, an unknown hub or
 *   sale) → a thrown `data(null, status)`: the route's ErrorBoundary, with
 *   the root's "Page not found" head (`pageMeta`);
 * - 503 → a thrown `data(null, 503)` with `Retry-After`;
 * - 200 → `{ head, preload }` with W1.3's Cache-Control;
 * - no load context (`react-router dev`, tests) → `{ head: null, preload:
 *   null }`: the page loads its data in the browser, as the SPA did.
 *
 * Pure module: no `window`, no React.
 */
import {
  data,
  redirect,
  type AppLoadContext,
  type UNSAFE_DataWithResponseInit as DataWithResponseInit,
} from 'react-router';
import { documentCacheControl } from '@/seo/documentHead';
import { entityHeaders, entityMeta } from '@/seo/entityDocument';
import type { RoutePreloadData } from '@/seo/routePreload';
import type { SeoHead } from '@/seo/templates';

/** A home / list / hub route's loader data. Both null after an in-app navigation (the page loads through Apollo). */
export interface PageRouteData {
  /** The server's head for this URL. */
  head: SeoHead | null;
  /** The page's query answers the server read, by `preloadKey`. */
  preload: RoutePreloadData | null;
}

export const EMPTY_PAGE_ROUTE_DATA: PageRouteData = Object.freeze({ head: null, preload: null });

/** The route loader (see the file comment). */
export async function loadPageDocument(
  context: AppLoadContext | undefined | null,
): Promise<PageRouteData | DataWithResponseInit<PageRouteData>> {
  const seo = context?.seo;
  if (!seo) return { head: null, preload: null };
  const decision = await seo.page();
  if (decision.location) {
    throw redirect(decision.location, {
      status: decision.status === 302 ? 302 : 301,
      headers: { 'Cache-Control': documentCacheControl(decision.status) },
    });
  }
  if (decision.status >= 400 || !decision.head) {
    const status = decision.status >= 400 ? decision.status : 500;
    throw data(null, {
      status,
      headers: {
        'Cache-Control': documentCacheControl(status),
        ...(decision.retryAfter ? { 'Retry-After': String(decision.retryAfter) } : {}),
      },
    });
  }
  return data<PageRouteData>(
    { head: decision.head, preload: decision.preload ?? {} },
    { status: 200, headers: { 'Cache-Control': documentCacheControl(200) } },
  );
}

/**
 * `clientLoader` for these routes: an in-app navigation never asks the SEO
 * server (its decisions are per document). No loader data: the page loads
 * through Apollo and renders its own head (`<PageHead/>`, web plan W5.1).
 */
export async function pageClientLoader(): Promise<PageRouteData> {
  return EMPTY_PAGE_ROUTE_DATA;
}

/** A thrown answer's headers win (a 503's `Retry-After`), else the loader's. */
export const pageHeaders = entityHeaders;

/**
 * The route's `meta`: its own head (the served document), else the root's
 * (an in-app navigation, a 404's "Page not found"), else nothing on an error
 * and the site defaults without the SEO server. Same rules as the entity
 * routes' (`entityMeta`, which reads only `data.head`).
 */
export const pageMeta = entityMeta;
